/**
 * One product card — used by the community **Shop** page, the product rails and
 * the product page (“more from this shop”).
 *
 * The button adds the product to **its own shop's** cart line: every cart item
 * keeps `shop_id` (`CartContext`), so products of different shops can sit in the
 * same cart and the checkout of each shop only takes its own lines.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingBag, FiShoppingCart } from 'react-icons/fi';
import { useCart } from '../../contexts/CartContext';
import { useBuy } from '../../contexts/BuyContext';
import { useCustomer } from '../../contexts/CustomerContext';
import { useLanguage } from '../../i18n';
import { fullUrl } from '../../api';
import { BLUE } from './brand';

export default function ShopProductCard({ product, className = '' }) {
  const { addItem } = useCart();
  const { askBuy } = useBuy();
  const { customer, isLoggedIn } = useCustomer();
  const { t } = useLanguage();
  const price = product.sale_price ?? product.price;
  const listPrice = product.price;
  const hasSale = product.sale_price != null && product.sale_price < listPrice;
  const image = (product.images || [])[0] || '';
  const shop = product.shop || {};
  const currency = shop.currency || 'USD';

  /**
   * Add it to **that shop's** cart. If the visitor has no account in that shop
   * yet, offer the “use your account to buy” modal (Google creates it) instead of
   * opening the drawer straight away.
   */
  const add = () => {
    const needsAccount = !(isLoggedIn && customer?.shop_id === product.shop_id);
    addItem(product, 1, {}, {}, { open: !needsAccount });
    if (needsAccount) askBuy({ id: product.shop_id, ...shop });
  };

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm hover:shadow-md transition group flex flex-col ${className}`}>
      <Link to={`/shop/product/${product.id}`} className="block relative">
        <div className="aspect-square bg-gray-100 dark:bg-gray-800 overflow-hidden">
          {image ? (
            <img
              src={fullUrl(image)}
              alt={product.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300 dark:text-gray-600">
              <FiShoppingBag className="w-10 h-10" />
            </div>
          )}
        </div>
        {hasSale && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {t('sale')}
          </span>
        )}
      </Link>

      <div className="p-2.5 flex flex-col gap-1.5 flex-1">
        <Link
          to={`/shop/product/${product.id}`}
          className="text-sm font-semibold leading-snug line-clamp-2 hover:underline"
        >
          {product.name}
        </Link>

        <div className="flex items-baseline gap-1.5">
          <span className="font-extrabold text-sm" style={{ color: BLUE }}>{price} {currency}</span>
          {hasSale && <span className="text-[11px] text-gray-400 line-through">{listPrice}</span>}
        </div>

        {shop.username && (
          <Link to={`/${shop.username}`} className="text-[11px] text-gray-400 truncate hover:underline">
            {shop.shop_name || shop.username}
          </Link>
        )}

        <button
          type="button"
          onClick={add}
          title={t('addToCart')}
          className="mt-auto w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white"
          style={{ background: BLUE }}
        >
          <FiShoppingCart className="w-3.5 h-3.5" /> {t('addToCart')}
        </button>
      </div>
    </div>
  );
}
